import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { Select } from '@/components/ui/Select';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { useOrg } from '@/contexts/OrgContext';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { canPostDocuments } from '@finvox/shared/org';

type Asset = {
  id: string;
  name: string;
  cost: string;
  accumulatedDepreciation: string;
  status: string;
};

export function FixedAssetsPage() {
  const { t } = useTranslation();
  const { orgId, org, currentPeriodId, periods } = useOrg();
  const online = useOnlineStatus();
  const toast = useToast();
  const [name, setName] = useState('');
  const [cost, setCost] = useState('1000.00');
  const [method, setMethod] = useState<'straight_line' | 'declining_balance'>('straight_line');
  const [busy, setBusy] = useState(false);

  const canPost = org ? canPostDocuments(org.role) : false;
  const periodOpen = periods.find((p) => p.id === currentPeriodId)?.status === 'open';

  const mapDoc = useCallback(
    (id: string, data: Record<string, unknown>) => ({
      id,
      name: String(data.name ?? ''),
      cost: String(data.cost ?? '0.00'),
      accumulatedDepreciation: String(data.accumulatedDepreciation ?? '0.00'),
      status: String(data.status ?? 'active')
    }),
    []
  );

  const {
    rows: assets,
    loading,
    pageIndex,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    pageSize,
    setPageSize,
    refresh
  } = useFirestorePageQuery<Asset>({
    orgId,
    collectionPath: 'fixedAssets',
    orderByField: 'name',
    orderDirection: 'asc',
    mapDoc
  });

  async function addAsset() {
    if (!orgId || !online || !name.trim()) return;
    const ref = doc(collection(getFirestoreDb(), 'organizations', orgId, 'fixedAssets'));
    await setDoc(ref, {
      name: name.trim(),
      cost,
      salvageValue: '0.00',
      usefulLifeMonths: 60,
      depreciationMethod: method,
      accumulatedDepreciation: '0.00',
      acquisitionDate: new Date().toISOString().slice(0, 10),
      status: 'active',
      createdAt: serverTimestamp()
    });
    setName('');
    toast.success(t('fixedAssets.saved'), 'fixed-assets');
    refresh();
  }

  async function runDepreciation() {
    if (!orgId || !currentPeriodId || !online) return;
    setBusy(true);
    try {
      const res = await apiPost<{ posted: number }>('/v1/fixed-assets/depreciate', {
        orgId,
        periodId: currentPeriodId
      });
      toast.success(t('fixedAssets.depreciationDone', { count: res.posted }), 'fixed-assets');
      refresh();
    } catch (e) {
      toastError(toast, e, 'fixed-assets');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.fixedAssets')}</h1>
      <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('fixedAssets.subtitle')}</p>

      {canPost && (
        <div className="mt-6 flex flex-wrap gap-2 rounded-lg border p-4">
          <input
            className="rounded border px-3 py-2 text-sm"
            placeholder={t('fixedAssets.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <MoneyInput
            className="w-32 text-sm"
            value={cost}
            onValueChange={setCost}
            disabled={!online}
            aria-label={t('fixedAssets.cost')}
          />
          <Select
            value={method}
            onValueChange={(v) => setMethod(v as typeof method)}
            options={[
              { value: 'straight_line', label: t('fixedAssets.straightLine') },
              { value: 'declining_balance', label: t('fixedAssets.decliningBalance') }
            ]}
          />
          <Button onClick={() => void addAsset()} disabled={!online}>
            {t('fixedAssets.add')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void runDepreciation()}
            disabled={!online || !periodOpen || busy}
          >
            {busy ? t('fixedAssets.running') : t('fixedAssets.depreciate')}
          </Button>
        </div>
      )}

      <PaginatedTable
        className="mt-6"
        loading={loading}
        pageIndex={pageIndex}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={nextPage}
        onPrev={prevPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-start">
              <th className="p-2">{t('fixedAssets.name')}</th>
              <th className="p-2 text-end">{t('fixedAssets.cost')}</th>
              <th className="p-2 text-end">{t('fixedAssets.accumDep')}</th>
              <th className="p-2">{t('journals.status')}</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.id} className="border-b border-[var(--fv-border)]">
                <td className="p-2">{a.name}</td>
                <td className="p-2 text-end"><MoneyDisplay value={a.cost} /></td>
                <td className="p-2 text-end"><MoneyDisplay value={a.accumulatedDepreciation} /></td>
                <td className="p-2">{a.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PaginatedTable>
    </div>
  );
}
